import { Link } from "wouter";
import {
  ArrowRight,
  BookOpenCheck,
  FileCheck2,
  HeartPulse,
  Leaf,
  LockKeyhole,
  ShieldCheck,
} from "lucide-react";
import Layout from "@/components/Layout";
import PageHero from "@/components/PageHero";
import SEOHead from "@/components/SEOHead";

const policyDocuments = [
  {
    href: "/kvalitetspolicy",
    title: "Kvalitetspolicy",
    category: "Verksamhet",
    description:
      "Våra principer för kundförståelse, leverans i rätt tid, nollfelsmål, kompetens och ständiga förbättringar.",
    icon: ShieldCheck,
  },
  {
    href: "/miljopolicy",
    title: "Miljöpolicy",
    category: "Verksamhet",
    description:
      "Hur vi arbetar med energi, resurser, kemikalier, avfall och minskad miljöpåverkan.",
    icon: Leaf,
  },
  {
    href: "/arbetsmiljopolicy",
    title: "Arbetsmiljöpolicy",
    category: "Verksamhet",
    description:
      "Våra åtaganden för en säker, hälsosam och utvecklande arbetsplats med mål om noll skador.",
    icon: HeartPulse,
  },
  {
    href: "/integritetspolicy",
    title: "Integritetspolicy och cookies",
    category: "Webbplats",
    description:
      "Hur vi hanterar personuppgifter och cookies samt vilka rättigheter du har enligt GDPR.",
    icon: LockKeyhole,
  },
  {
    href: "/villkor",
    title: "Allmänna villkor",
    category: "Affärsvillkor",
    description:
      "Villkor för offerter, beställningar, underlag, leveranser, betalning och reklamationer.",
    icon: FileCheck2,
  },
  {
    href: "/redaktionell-policy",
    title: "Redaktionell policy",
    category: "Webbplats",
    description:
      "Principerna bakom innehållet på aspab.com och hur tekniska uppgifter faktagranskas och uppdateras.",
    icon: BookOpenCheck,
  },
];

export default function Policyer() {
  return (
    <Layout>
      <SEOHead
        title="Policyer och villkor | ASPAB"
        description="Här hittar du ASPAB:s kvalitets-, miljö- och arbetsmiljöpolicy samt integritetspolicy, redaktionella riktlinjer och allmänna villkor."
        canonical="https://aspab.com/policyer"
        path="/policyer"
        breadcrumbs={[{ name: "Policyer och villkor", href: "/policyer" }]}
        ogImage="/images/og-default-4A6pZMTTPtiqZ6M3P4uLnb.png"
      />

      <PageHero
        title="Policyer och villkor"
        subtitle="Samlad dokumentation om hur ASPAB arbetar, tar ansvar och hanterar affärs- och webbplatsfrågor."
        breadcrumbs={[{ name: "Policyer och villkor", href: "/policyer" }]}
      />

      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <div className="mx-auto max-w-3xl text-center">
            <span className="text-sm font-semibold uppercase tracking-[0.16em] text-[#2563EB]">
              Samlad dokumentation
            </span>
            <h2 className="mt-3 font-heading text-3xl font-bold text-gray-900 md:text-4xl">
              Tydliga principer för verksamhet och samarbete
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-gray-600">
              Här hittar du våra styrande policyer, villkor och redaktionella riktlinjer.
              Välj ett dokument för att läsa det i sin helhet.
            </p>
          </div>

          <div className="mx-auto mt-12 grid max-w-6xl gap-5 md:grid-cols-2 xl:grid-cols-3">
            {policyDocuments.map(document => {
              const Icon = document.icon;
              return (
                <Link
                  key={document.href}
                  href={document.href}
                  className="group flex min-h-64 flex-col rounded-xl border border-gray-200 bg-white p-6 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:border-blue-200 hover:shadow-xl hover:shadow-blue-950/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-offset-4"
                >
                  <div className="flex items-start justify-between gap-4">
                    <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-[#2563EB] transition-colors group-hover:bg-[#2563EB] group-hover:text-white">
                      <Icon className="h-6 w-6" aria-hidden="true" />
                    </span>
                    <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-gray-500">
                      {document.category}
                    </span>
                  </div>

                  <h3 className="mt-6 font-heading text-xl font-bold text-gray-900 transition-colors group-hover:text-[#2563EB]">
                    {document.title}
                  </h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-gray-600">
                    {document.description}
                  </p>
                  <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#2563EB]">
                    Läs dokumentet
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </Layout>
  );
}
